import {
    FormAction,
    FormState,
    PageActionType,
    ValueActionType
} from "./types";
import {
    lastPageIndex,
    pageAfter,
    pageBefore,
    validator
} from "./form-context";

const createStateReducer = (
    reducers: Array<(state: FormState, action: FormAction) => FormState>
) => (state: FormState, action: FormAction) => {
    const r = reducers.reduce((s, r) => r(s, action), state);
    return r;
};

/**
 * Value Reducers
 */

const assignValuesReducer = (state: FormState, action: FormAction) =>
    action.type === ValueActionType.Assign
        ? {
              ...state,
              values: {
                  ...state.values,
                  ...action.body
              }
          }
        : state;

const setValueReducer = (state: FormState, action: FormAction) =>
    action.type === ValueActionType.Set
        ? {
              ...state,
              values: {
                  ...state.values,
                  [action.body.name]: action.body.value
              }
          }
        : state;

const unsetValueReducer = (state: FormState, action: FormAction) => {
    if (action.type === ValueActionType.Unset) {
        const { [action.body]: _, ...values } = state.values;
        return {
            ...state,
            values
        };
    }
    return state;
};

const valuesReducer = (state: FormState, action: FormAction) => {
    return [setValueReducer, assignValuesReducer, unsetValueReducer].reduce(
        (s, r) => r(s, action),
        state
    );
};

/**
 * Validation Reducer
 */

const validationReducer = (state: FormState, action: FormAction) => {
    if (action.type === ValueActionType.Assign
        || action.type === ValueActionType.Set
        || action.type === ValueActionType.Unset) {
        return {
            ...state,
            validation: validator(state)
        };
    }
    return state;
};

/**
 * Paging reducers
 */

const gotoPageReducer = (state: FormState, action: FormAction) => {
    if (action.type === PageActionType.GotoPage) {
        return {
            ...state,
            page: action.body
        };
    }
    return state;
};

const gotoNextPageReducer = (state: FormState, action: FormAction) => {
    if (action.type === PageActionType.GotoNext) {
        const nextPage = pageAfter(state, state.page);
        return {
            ...state,
            page: nextPage,
            visitedLastPage:
                state.visitedLastPage ||
                lastPageIndex(state.pagination, state) === nextPage
        };
    }
    return state;
};

const gotoPreviousPageReducer = (state: FormState, action: FormAction) => {
    if (action.type === PageActionType.GotoPrevious) {
        return {
            ...state,
            page: pageBefore(state, state.page)
        };
    }
    return state;
};

const gotoReducer = (state: FormState, action: FormAction) => {
    return [
        gotoPageReducer,
        gotoNextPageReducer,
        gotoPreviousPageReducer
    ].reduce((s, r) => r(s, action), state);
};

export {
    createStateReducer,
    setValueReducer,
    assignValuesReducer,
    unsetValueReducer,
    valuesReducer,
    gotoPageReducer,
    gotoNextPageReducer,
    gotoPreviousPageReducer,
    gotoReducer,
    validationReducer
};
